/**
 * A full-viewport message: used for boot, fatal configuration errors, and
 * not-found states where there is no navigation chrome to show.
 *
 * Nothing here needs the theme through JavaScript — the surface, the icon
 * colour, and the action button are all token classes, so the component follows
 * dark mode without reading the mode.
 */

import { View } from 'react-native';
import { Info, type LucideIcon } from 'lucide-react-native';

import { Button, Icon, Stack, Text, cn } from '@yummy/ui-rnr';

/**
 * Centres an icon, title, message, and optional action in the viewport.
 *
 * @param props Message content.
 */
export function FullScreenMessage({
  icon: IconComponent = Info,
  title,
  message,
  actionLabel,
  onAction,
  tone = 'neutral',
}: {
  /** The mark above the title; defaults to an information circle. */
  icon?: LucideIcon;
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <View className="bg-background flex-1 items-center justify-center p-6">
      <Stack gap="md" align="center" justify="center" className="max-w-[420px]">
        <Icon
          as={IconComponent}
          size={40}
          strokeWidth={1.5}
          aria-hidden
          className={cn(tone === 'danger' ? 'text-destructive' : 'text-subtle-foreground')}
        />
        <Text className="text-center text-2xl font-semibold">{title}</Text>
        {message ? (
          <Text className="text-muted-foreground text-center text-base">{message}</Text>
        ) : null}
        {actionLabel && onAction ? (
          <Button variant="secondary" onPress={onAction}>
            <Text>{actionLabel}</Text>
          </Button>
        ) : null}
      </Stack>
    </View>
  );
}
